import { Suspense, lazy } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'

import { useLogout, useMe } from './hooks/queries'
import { useOutboxVersand } from './hooks/useOutbox'
import { Shell } from './features/shell/Shell'
import { LoginPage } from './features/auth/LoginPage'
import { RegisterPage } from './features/auth/RegisterPage'
import { JoinPage } from './features/auth/JoinPage'
import { ForgotPage } from './features/auth/ForgotPage'
import { ResetPage } from './features/auth/ResetPage'
import { ThreadPage } from './features/thread/ThreadPage'
import { NewTripPage } from './features/trips/NewTripPage'
import { TripInfoPage } from './features/trips/TripInfoPage'
import { SettingsPage } from './features/settings/SettingsPage'
import { PeoplePage } from './features/settings/PeoplePage'
import { StartPane } from './features/shell/StartPane'
import { StandWache } from './features/shell/StandWache'

/**
 * Die Karte kommt erst, wenn jemand sie öffnet.
 *
 * Leaflet wiegt rund 150 kB — mehr als ein Drittel der bisherigen Anwendung.
 * Wer nur liest und antwortet, soll das nicht über Mobilfunk mitladen. Der
 * Chat ist der Alltag, die Karte ist der Besuch.
 */
const KartePage = lazy(() =>
  import('./features/map/KartePage').then((m) => ({ default: m.KartePage })),
)

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot" element={<ForgotPage />} />
      {/* Der Link aus der E-Mail. Er trägt das Token im Pfad und funktioniert
          ohne Anmeldung -- wer sich anmelden könnte, bräuchte ihn nicht. */}
      <Route path="/reset/:token" element={<ResetPage />} />
      {/* Der Einladungslink funktioniert auch ohne Konto -- wer ihn öffnet,
          wird zuerst durch Registrierung oder Anmeldung geführt. */}
      <Route path="/join/:code" element={<JoinPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<Shell />}>
          <Route index element={<StartPane />} />
          <Route path="t/:tripId" element={<ThreadPage />} />
          <Route path="t/:tripId/with/:userId" element={<ThreadPage />} />
          <Route
            path="t/:tripId/karte"
            element={
              <Suspense fallback={<div className="empty">Karte wird geladen …</div>}>
                <KartePage />
              </Suspense>
            }
          />
          <Route path="t/:tripId/info" element={<TripInfoPage />} />
          <Route path="new" element={<NewTripPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/people" element={<PeoplePage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

/**
 * Schleuse vor allem, was eine Anmeldung braucht.
 *
 * Der Weg, den jemand aufgerufen hat, wird an die Anmeldeseite weitergereicht,
 * damit ein geteilter Link nach dem Anmelden nicht auf der Startseite endet.
 */
function RequireAuth() {
  const location = useLocation()
  const me = useMe()
  // Der Postbote sitzt hier und nicht im Chat: was ohne Netz geschrieben
  // wurde, soll auch dann rausgehen, wenn die App auf der Liste steht.
  useOutboxVersand()

  if (me.isPending) {
    return (
      <div className="centered">
        <p className="empty__title">Einen Moment …</p>
      </div>
    )
  }

  if (me.isError || !me.data) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }

  // Wer angemeldet ist, aber in keiner Reise steht, kommt nicht in die
  // Anwendung -- auch nicht in eine leere. Das ist der Fall des Fremden vom
  // weitergereichten Link: sein Konto gibt es, hereingelassen hat ihn niemand.
  if (!me.data.may_use_app) {
    return (
      <>
        <StandWache />
        <Wartetafel />
      </>
    )
  }

  return (
    <>
      <StandWache />
      <Outlet />
    </>
  )
}

/**
 * Was jemand sieht, der angemeldet ist und auf eine Bestätigung wartet.
 *
 * Nur diese eine Karte, nicht die Chatliste dahinter. Abmelden geht --
 * jemanden anzumelden und ihm dann keinen Ausgang zu lassen, wäre eine Falle.
 *
 * Wer abgelehnt wurde, sieht dieselbe Tafel. Das ist keine Nachlässigkeit,
 * sondern dieselbe Entscheidung wie überall: eine mitgeteilte Ablehnung ist
 * eine Aufforderung nachzufragen.
 */
function Wartetafel() {
  const me = useMe()
  const logout = useLogout()
  const wartet = me.data?.waiting_for ?? null

  return (
    <div className="centered">
      <div className="card">
        <h1>{wartet?.trip_title ?? 'Noch nichts freigegeben'}</h1>
        <p className="card__lead">
          {wartet
            ? `Deine Anfrage ist bei ${wartet.owner_name}. Sobald sie bestätigt ist, steht der Reisebericht hier.`
            : 'Sobald dich jemand zu einem Reisebericht einlädt, geht es hier weiter.'}
        </p>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Angemeldet als {me.data?.display_name} ({me.data?.email})
        </p>
        <button
          className="button button--outline"
          type="button"
          disabled={logout.isPending}
          onClick={() => logout.mutate()}
        >
          Abmelden
        </button>
      </div>
    </div>
  )
}
